import type { Dayjs } from 'dayjs';

import type { DialogType } from '../../../@types';
import type { BonusProductType, BonusStatusCustom, BonusStatusOngoing, BonusTimeframe } from '../../../@types/api';
import { BONUS_TIMEFRAME_OPTIONS, CASHBACK_BONUS_STATUS_FILTER_OPTIONS } from '../../../constants';
import type { VALIDITY_PERIOD } from '../../../constants/enums';
import useFilterForm from '../../../hooks/useFilterForm';
import { useMarketplace } from '../../../hooks/useMarketplace';
import { getCashbackBonusProductOptions } from '../../../utils/cashbackBonus';
import type { AdditionalQueryParams } from '../../../hooks/usePagination';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormSelect from '../../molecules/FormSelect';
import ValidityPeriodSelect from '../../molecules/ValidityPeriodSelect';

type CashbackBonusFilterFormData = {
  product?: BonusProductType;
  status?: BonusStatusCustom | BonusStatusOngoing;
  validityPeriod?: (typeof VALIDITY_PERIOD)[keyof typeof VALIDITY_PERIOD];
  schedule?: BonusTimeframe;
  fromTimestamp?: Dayjs;
  toTimestamp?: Dayjs;
};

const DEFAULT_FORM_DATA: CashbackBonusFilterFormData = {
  product: undefined,
  status: undefined,
  validityPeriod: undefined,
  schedule: undefined,
  fromTimestamp: undefined,
  toTimestamp: undefined,
};

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterCashbackBonuses = ({ closeModal, changeQuery }: Props) => {
  const { config } = useMarketplace();
  // Existing campaigns remain filterable regardless of the creation rollout flag.
  const productOptions = getCashbackBonusProductOptions(true, undefined, config?.marketplaceConfig?.products);
  const { control, handleSubmit, handleResetFilter } = useFilterForm<CashbackBonusFilterFormData>({
    defaultData: DEFAULT_FORM_DATA,
    changeQuery,
    prepareQueryParams: (data) => ({
      ...data,
      product: productOptions.some((option) => option.id === data.product) ? data.product : undefined,
      fromTimestamp: data.fromTimestamp?.valueOf(),
      toTimestamp: data.toTimestamp?.valueOf(),
    }),
  });

  return (
    <FilterDialogLayout label='cashback bonuses' onClose={closeModal} onSave={handleSubmit} onReset={handleResetFilter}>
      <FormSelect control={control} name='product' label='Product' options={productOptions} />
      <FormSelect control={control} name='status' label='Status' options={CASHBACK_BONUS_STATUS_FILTER_OPTIONS} />
      <ValidityPeriodSelect control={control} />
      <FormSelect control={control} name='schedule' label='Timeframe' options={BONUS_TIMEFRAME_OPTIONS} />
    </FilterDialogLayout>
  );
};

export default FilterCashbackBonuses;
